import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useEffectiveRole } from "../../lib/roles";
import { useTenantTemplateSummary } from "./hooks/useTenantTemplateSummary";
import {
  isValidPanelKey,
  PANEL_KEYS,
  panelForItem,
  panelTitle,
  type PanelKey,
} from "./lib/contracts";
import { OnboardingProgress } from "./components/OnboardingProgress";
import { OnboardingPanel } from "./components/OnboardingPanel";
import { ReadOnlyOnboardingNotice } from "./components/ReadOnlyOnboardingNotice";
import { TemplateSelectionPanel } from "./components/TemplateSelectionPanel";
import { CompletionSection } from "./components/CompletionSection";
import { OwnerOnboardingWizard } from "./components/OwnerOnboardingWizard";
import { useOnboarding } from "./lib/OnboardingProvider";

export function OnboardingWorkspaceContent() {
  const role = useEffectiveRole();
  const isRestricted = role === "representative" || role === "viewer";
  const {
    state,
    loading,
    error,
    refresh,
    acknowledgeTenantProfile,
    ackPending,
    conflictMessage,
    clearConflict,
  } = useOnboarding();
  const [params, setParams] = useSearchParams();

  const firstIncompletePanel = useMemo<PanelKey>(() => {
    if (!state) return "tenant-profile";
    if (state.overall_status === "completed") return "tenant-profile";
    const firstIncomplete = state.items.find((i) => i.status !== "complete");
    return (firstIncomplete && panelForItem(firstIncomplete.key)) || "tenant-profile";
  }, [state]);

  const rawPanel = params.get("panel");
  const activePanel: PanelKey = isValidPanelKey(rawPanel)
    ? rawPanel
    : firstIncompletePanel;

  useEffect(() => {
    if (!state) return;
    if (!isValidPanelKey(rawPanel)) {
      const next = new URLSearchParams(params);
      next.set("panel", firstIncompletePanel);
      setParams(next, { replace: true });
    }
  }, [state, rawPanel, params, setParams, firstIncompletePanel]);

  const setPanel = (p: PanelKey) => {
    const next = new URLSearchParams(params);
    next.set("panel", p);
    setParams(next, { replace: true });
  };

  const selectedTemplateKey = state?.selected_template_key ?? null;
  const { summary: templateSummary, error: templateError } =
    useTenantTemplateSummary(selectedTemplateKey, isRestricted);

  if (loading && !state) {
    return (
      <div
        className="p-6 text-sm text-slate-500"
        data-testid="onboarding-loading"
      >
        Loading onboarding status…
      </div>
    );
  }

  if (isRestricted) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <ReadOnlyOnboardingNotice />
      </div>
    );
  }

  if (!state) {
    return (
      <div
        className="p-6 text-sm text-red-700"
        data-testid="onboarding-error"
      >
        {error || "Unable to load onboarding status."}
        <button
          type="button"
          onClick={refresh}
          className="ml-3 underline"
          data-testid="onboarding-refresh-error"
        >
          Retry
        </button>
      </div>
    );
  }

  const canAcknowledge = role === "owner_admin";
  const panelRole =
    role === "owner_admin"
      ? "owner_admin"
      : role === "manager_trainer"
      ? "manager_trainer"
      : role === "super_admin"
      ? "owner_admin"
      : "read_only";

  const activeItems = state.items.filter(
    (it) => panelForItem(it.key) === activePanel,
  );

  return (
    <div
      className="mx-auto max-w-6xl p-4 md:p-6"
      data-testid="onboarding-workspace"
      role="region"
      aria-label="Onboarding workspace"
    >
      <header className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Tenant setup</h1>
          <p className="mt-1 text-sm text-slate-500">
            Review each area of setup and confirm the tenant is ready for
            everyday use.
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          className="rounded-md border px-3 py-1.5 text-sm dark:border-slate-700"
          data-testid="onboarding-refresh-button"
        >
          Refresh setup status
        </button>
      </header>

      {error && (
        <div
          className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200"
          data-testid="onboarding-inline-error"
          role="alert"
        >
          {error}
        </div>
      )}

      {conflictMessage && (
        <div
          className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300"
          data-testid="onboarding-conflict-message"
          role="status"
          aria-live="polite"
        >
          {conflictMessage}
          <button
            type="button"
            onClick={clearConflict}
            className="ml-3 underline"
            data-testid="onboarding-conflict-dismiss"
          >
            Dismiss
          </button>
        </div>
      )}

      {state.overall_status === "completed" && (
        <div
          className="mb-4 rounded-md border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-900 dark:bg-emerald-950"
          data-testid="onboarding-completed-summary"
          role="status"
        >
          <p className="text-sm font-medium text-emerald-800 dark:text-emerald-200">
            Onboarding complete.
            {state.completed_at ? ` Marked complete at ${state.completed_at}.` : ""}
          </p>
          {state.selected_template_key && (
            <p className="mt-1 text-xs text-emerald-800 dark:text-emerald-300">
              Selected template: {state.selected_template_key} v
              {state.selected_template_version}
            </p>
          )}
        </div>
      )}

      {role === "owner_admin" && <OwnerOnboardingWizard />}

      {/* Completion section — visible for Owner/Admin when ready, or the
          post-completion summary when overall_status === "completed". */}
      <div className="mb-4">
        <CompletionSection />
      </div>

      <div className="grid gap-4 md:grid-cols-[minmax(0,16rem)_minmax(0,1fr)]">
        <OnboardingProgress
          state={state}
          activePanel={activePanel}
          onSelectPanel={setPanel}
        />
        <div>
          <a
            href={`#panel-heading-${activePanel}`}
            className="sr-only focus:not-sr-only focus:mb-2 focus:inline-block focus:rounded focus:bg-slate-900 focus:px-2 focus:py-1 focus:text-white"
            data-testid="onboarding-skip-to-panel"
          >
            Skip to {panelTitle(activePanel)}
          </a>
          <OnboardingPanel
            panelKey={activePanel}
            items={activeItems}
            role={panelRole}
            effectiveRole={role}
            onAcknowledgeTenantProfile={
              canAcknowledge && activePanel === "tenant-profile"
                ? () => acknowledgeTenantProfile(true)
                : undefined
            }
            ackPending={ackPending}
            templateSummary={templateSummary}
            templateSummaryError={templateError}
          />
          {/* Template application UI — visible only when the template
              panel is active and the tenant has NOT yet been stamped with
              a template. */}
          {activePanel === "template" && !state.selected_template_key && (
            <div className="mt-4">
              <TemplateSelectionPanel />
            </div>
          )}
          <div className="sr-only" aria-hidden="true">
            {PANEL_KEYS.filter((p) => p !== activePanel).map((p) => (
              <span key={p} id={`panel-heading-${p}`} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function OnboardingWorkspace() {
  return <OnboardingWorkspaceContent />;
}
